// Readable category addresses, e.g. /shop/power-supplies. Kept free of React
// so the build script (scripts/page-html.js) can write a page per category.
export type CategoryRecord = { _id: string; name: string; subcategory?: CategoryRecord[] };
export type CategoryPage = { id: string; slug: string; label: string; parentLabel?: string };

/** Display name for a category stored in capitals, e.g. "LED LIGHTINGS" → "LED Lighting". */
export const categoryLabel = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase())
    .replace(/\b(Lcd|Led|Usb|Sas|Cga|Vga|Pcb)\b/g, (c) => c.toUpperCase())
    .replace(/LED Lightings/g, "LED Lighting")
    .replace(/Connnectors/g, "Connectors")
    .replace(/Wifi/g, "Wi-Fi");

export const slugify = (text: string) =>
  text.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "category";

export const isCategoryId = (value: string) => /^[a-f0-9]{24}$/i.test(value);

/**
 * Every category and subcategory with its address. A name used twice keeps
 * its plain address for the first and adds the end of its id for the others,
 * so addresses don't change when categories are reordered.
 */
export function categoryPages(categories: CategoryRecord[]): CategoryPage[] {
  const out: CategoryPage[] = [];
  const used = new Set<string>();
  const add = (c: CategoryRecord, parentLabel?: string) => {
    if (!c || typeof c._id !== "string" || typeof c.name !== "string" || !c.name.trim()) return;
    const label = categoryLabel(c.name);
    let slug = slugify(label);
    if (used.has(slug)) slug = `${slug}-${c._id.slice(-4).toLowerCase()}`;
    used.add(slug);
    out.push({ id: c._id, slug, label, ...(parentLabel ? { parentLabel } : {}) });
  };
  const list = Array.isArray(categories) ? categories : [];
  for (const c of list) add(c);
  for (const c of list) for (const s of Array.isArray(c?.subcategory) ? c.subcategory : []) add(s, categoryLabel(c.name));
  return out;
}
